/**
 * Bra Size Selector Module
 * Handles band size, cup size selection and matches to correct product variant
 * For Shopify product pages
 */

class BraSizeSelector {
  constructor(containerSelector, productData) {
    this.container = document.querySelector(containerSelector);
    if (!this.container) return;
    
    this.productData = productData;
    this.productType = this.getProductType();

    // Per-product, per-color size adjustments
    // key: product title substring (lowercase), value: array of color groups with size offset
    this.productOverrides = {
      'woven selene top d/dd(e)/ddd(f)': {
        adjustedOnly: true,
      },
      'selene top d/dd(e)/ddd(f)': {
        defaultColors: ['kinski python'],          // colors that follow normal sizing
        adjustedColors: ['charlie green', 'black'], // colors that run small (size up by 1)
        adjustedLabel: 'BLACK & CHARLIE GREEN',
        defaultLabel: 'KINSKI PYTHON',
      }
    };

    // Shown below the normal result when cup size is C — only on the smaller version of paired styles.
    this.cUpgradeProducts = {
      'lots of love a/b/c': { name: 'Pamela C/D/DD',       url: '/products/pamela-c-d' },
      'bralette b/c':       { name: 'Bralette C/D/DD',      url: '/products/bra-size-swimwear-tops-d-cup-bralette' },
      'raquel a/b/c':       { name: 'Raquel C/D',           url: '/products/raquel-top-c-d-dd-cup' },
      'lola a/b/c':         { name: 'Lola C/D/DD',          url: '/products/lola-c-d-dd' },
    };

    // Keys must be lowercase substrings of the Shopify product title.
    // If a suggestion isn't appearing, check that the key matches the actual title exactly.
    this.companionProducts = {
      'damita a/b/c':   { name: 'Damita D/DD',       url: '/products/damita-d-dd',            cupSizes: ['D', 'DD(E)'] },
      'damita d/dd':    { name: 'Damita A/B/C',       url: '/products/damita-a-b-c',           cupSizes: ['A', 'B', 'C'] },
      'juliette a/b/c': { name: 'Juliette D/DD/E',    url: '/products/juliette-d-dd',          cupSizes: ['D', 'DD(E)', 'DDD(F)'] },
      'juliette d/dd':  { name: 'Juliette A/B/C',     url: '/products/juliette-top-a-b-c-cup', cupSizes: ['A', 'B', 'C'] },
      'lara a/b':       { name: 'Lara C/D/DD',        url: '/products/lara-top-c-d-dd-cup',    cupSizes: ['C', 'D', 'DD(E)'] },
      'lara c/d':       { name: 'Lara A/B',           url: '/products/lara-top-a-b',           cupSizes: ['A', 'B'] },
      'riley a/b':      { name: 'Riley C/D',          url: '/products/riley-top-c-d-cup',      cupSizes: ['C', 'D'] },
      'riley c/d':      { name: 'Riley A/B',          url: '/products/riley-top-a-b',          cupSizes: ['A', 'B'] },
      'lots of love a/b/c': { name: 'Pamela C/D/DD',      url: '/products/pamela-c-d',                             cupSizes: ['D', 'DD(E)'] },
      'pamela c/d':         { name: 'Lots of Love A/B/C', url: '/products/lots-of-love-a-b-c',                     cupSizes: ['A', 'B'] },
      'bralette b/c':       { name: 'Bralette C/D/DD',    url: '/products/bra-size-swimwear-tops-d-cup-bralette',  cupSizes: ['D', 'DD(E)'] },
      'bralette c/d/dd':     { name: 'Bralette B/C',       url: '/products/bra-size-swimwear-tops-bralette-b-c',    cupSizes: ['A', 'B'] },
      'raquel a/b/c':       { name: 'Raquel C/D',         url: '/products/raquel-top-c-d-dd-cup',                  cupSizes: ['D', 'DD(E)'] },
      'raquel c/d':         { name: 'Raquel A/B/C',       url: '/products/raquel-top-a-b-c-cup',                   cupSizes: ['A', 'B'] },
      'lola a/b/c':         { name: 'Lola C/D/DD',        url: '/products/lola-c-d-dd',                            cupSizes: ['D', 'DD(E)'] },
      'lola c/d':           { name: 'Lola A/B/C',         url: '/products/lola-a-b-c',                             cupSizes: ['A', 'B'] },
    };
    
    // Size matrix mapping: band size → cup size → cup type → variant info
    this.sizeMatrixTops = {
      '28': {
        'A': {},
        'B': { 'AB': { size: '30', fits: '30A/28B' } },
        'C': { 'ABC': { size: '30', fits: '32A/30B/28C' }, 'BC': { size: '30', fits: '30B/28C' } },
        'D': { 'CD': { size: '30', fits: '30C/28D' } },
        'DD(E)': { 'CDDD': { size: '30', fits: '32C/30D/28DD(E)' } },
        'DDD(F)': { 'DD/E/F': { size: '30', fits: '30DD(E)/28DDD(F)' }, 'D/E/F': { size: '30', fits: '32D/30DD(E)/28DDD(F)' } }
      },
      '30': {
        'A': { 'AB': { size: '30', fits: '30A/28B' } },
        'B': { 'ABCD': { size: '30', fits: '32A/30B/30C' }, 'AB': { size: '32', fits: '32A/30B' }, 'ABC': { size: '30', fits: '32A/30B/28C' }, 'BC': { size: '30', fits: '30B/28C' } },
        'C': { 'ABCD': { size: '30', fits: '32A/30B/30C' }, 'ABC': { size: '32', fits: '34A/32B/30C' }, 'BC': { size: '32', fits: '32B/30C' }, 'CD': { size: '30', fits: '30C/28D' } },
        'D': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'BC': { size: '32', fits: '32C/30D' }, 'CD': { size: '30', fits: '32C/30D/28DD(E)' } },
        'DD(E)': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'CDDD': { size: '32', fits: '34C/32D/30DD(E)' }, 'CDDD-pam': { size: '30', fits: '34C/32D/30DD(E)' }, 'D/DD': { size: '30', fits: '32D/30DD(E)' }, 'DD/E/F': { size: '30', fits: '30DD(E)/28DDD(F)' }, 'D/E/F': { size: '30', fits: '32D/30DD(E)/28DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '32', fits: '32DD(E)/30DDD(F)' }, 'D/E/F': { size: '32', fits: '34D/32DD(E)/30DDD(F)' } }
      },
      '32': {
        'A': { 'ABCD': { size: '30', fits: '32A/30B/30C' }, 'AB': { size: '32', fits: '32A/30B' }, 'ABC': { size: '30', fits: '32A/30B/28C' } },
        'B': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'AB': { size: '34', fits: '34A/32B' }, 'ABC': { size: '32', fits: '34A/32B/30C' }, 'BC': { size: '32', fits: '32B/30C' } },
        'C': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'ABC': { size: '34', fits: '36A/34B/32C' }, 'BC': { size: '34', fits: '34B/32C' }, 'CD': { size: '32', fits: '32C/30D' }, 'CDDD': { size: '30', fits: '32C/30D/28DD(E)' } },
        'D': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'CD': { size: '34', fits: '34C/32D' }, 'CDDD': { size: '32', fits: '34C/32D/30DD(E)' }, 'CDDD-pam': { size: '30', fits: '34C/32D/30DD(E)' }, 'D/DD': { size: '30', fits: '32D/30DD(E)' }, 'D/E/F': { size: '30', fits: '32D/30DD(E)/28DDD(F)' } },
        'DD(E)': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'CDDD': { size: '34', fits: '36C/34D/32DD(E)' }, 'CDDD-pam': { size: '32', fits: '36C/34D/32DD(E)' }, 'D/DD': { size: '32', fits: '34D/32DD(E)' }, 'DD/E/F': { size: '32', fits: '32DD(E)/30DDD(F)' }, 'D/E/F': { size: '32', fits: '34D/32DD(E)/30DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '34', fits: '34DD(E)/32DDD(F)' }, 'D/E/F': { size: '34', fits: '36D/34DD(E)/32DDD(F)' } }
      },
      '34': {
        'A': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'AB': { size: '34', fits: '34A/32B' }, 'ABC': { size: '32', fits: '34A/32B/30C' } },
        'B': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'AB': { size: '36', fits: '36A/34B' }, 'ABC': { size: '34', fits: '36A/34B/32C' }, 'BC': { size: '34', fits: '34B/32C' } },
        'C': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'ABC': { size: '36', fits: '38A/36B/34C' }, 'BC': { size: '36', fits: '36B/34C' }, 'CD': { size: '34', fits: '34C/32D' }, 'CDDD': { size: '32', fits: '34C/32D/30DD(E)' }, 'CDDD-pam': { size: '30', fits: '34C/32D/30DD(E)' }  },
        'D': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'CD': { size: '36', fits: '36C/34D' }, 'CDDD': { size: '34', fits: '36C/34D/32DD(E)' }, 'CDDD-pam': { size: '32', fits: '36C/34D/32DD(E)' }, 'D/DD': { size: '32', fits: '34D/32DD(E)' }, 'D/E/F': { size: '32', fits: '34D/32DD(E)/30DDD(F)' } },
        'DD(E)': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'CDDD': { size: '36', fits: '38C/36D/34DD(E)' }, 'CDDD-pam': { size: '34', fits: '38C/36D/34DD(E)' }, 'D/DD': { size: '34', fits: '36D/34DD(E)' }, 'DD/E/F': { size: '34', fits: '34DD(E)/32DDD(F)' }, 'D/E/F': { size: '34', fits: '36D/34DD(E)/32DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '36', fits: '36DD(E)/34DDD(F)' }, 'D/E/F': { size: '36', fits: '38D/36DD(E)/34DDD(F)' } }
      },
      '36': {
        'A': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'AB': { size: '36', fits: '36A/34B' }, 'ABC': { size: '34', fits: '36A/34B/32C' } },
        'B': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'AB': { size: '38', fits: '38A/36B' }, 'ABC': { size: '36', fits: '38A/36B/34C' }, 'BC': { size: '36', fits: '36B/34C' } },
        'C': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'ABC': { size: '38', fits: '40A/38B/36C' }, 'BC': { size: '38', fits: '38B/36C' }, 'CD': { size: '36', fits: '36C/34D' }, 'CDDD': { size: '34', fits: '36C/34D/32DD(E)' }, 'CDDD-pam': { size: '32', fits: '36C/34D/32DD(E)' } },
        'D': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'CD': { size: '38', fits: '38C/36D' }, 'CDDD': { size: '36', fits: '38C/36D/34DD(E)' }, 'CDDD-pam': { size: '34', fits: '38C/36D/34DD(E)' }, 'D/DD': { size: '34', fits: '36D/34DD(E)' }, 'DD/E/F': { size: '34', fits: '34DD(E)/32DDD(F)' }, 'D/E/F': { size: '34', fits: '36D/34DD(E)/32DDD(F)' } },
        'DD(E)': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'CDDD': { size: '38', fits: '40C/38D/36DD(E)' }, 'CDDD-pam': { size: '36', fits: '40C/38D/36DD(E)' }, 'D/DD': { size: '36', fits: '38D/36DD(E)' }, 'DD/E/F': { size: '36', fits: '36DD(E)/34DDD(F)' }, 'D/E/F': { size: '36', fits: '38D/36DD(E)/34DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '38', fits: '38DD(E)/36DDD(F)' }, 'D/E/F': { size: '38', fits: '40D/38DD(E)/36DDD(F)' } }
      },
      '38': {
        'A': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'AB': { size: '38', fits: '38A/36B' }, 'ABC': { size: '36', fits: '38A/36B/34C' } },
        'B': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'ABC': { size: '38', fits: '40A/38B/36C' }, 'BC': { size: '38', fits: '38B/36C' } },
        'C': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'CD': { size: '38', fits: '38C/36D' }, 'CDDD': { size: '36', fits: '38C/36D/34DD(E)' }, 'CDDD-pam': { size: '34', fits: '38C/36D/34DD(E)' } },
        'D': { 'CDDD': { size: '38', fits: '40C/38D/36DD(E)' }, 'CDDD-pam': { size: '36', fits: '40C/38D/36DD(E)' }, 'D/DD': { size: '36', fits: '38D/36DD(E)' }, 'DD/E/F': { size: '36', fits: '36DD(E)/34DDD(F)' }, 'D/E/F': { size: '36', fits: '38D/36DD(E)/34DDD(F)' } },
        'DD(E)': { 'CDDD': { size: '40', fits: '42C/40D/38DD(E)' }, 'CDDD-pam': { size: '38', fits: '42C/40D/38DD(E)' }, 'D/DD': { size: '38', fits: '40D/38DD(E)' }, 'DD/E/F': { size: '38', fits: '38DD(E)/36DDD(F)' }, 'D/E/F': { size: '38', fits: '40D/38DD(E)/36DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '40', fits: '40DD(E)/38DDD(F)' }, 'D/E/F': { size: '40', fits: '42D/40DD(E)/38DDD(F)' } }
      },
      '40': {
        'A': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'ABC': { size: '38', fits: '40A/38B/36C' } },
        'B': {},
        'C': { 'CD': { size: '38', fits: '40C/38D/36DD(E)' }, 'CDDD-pam': { size: '36', fits: '40C/38D/36DD(E)' } },
        'D': { 'CDDD': { size: '40', fits: '42C/40D/38DD(E)' }, 'CDDD-pam': { size: '38', fits: '42C/40D/38DD(E)' }, 'D/DD': { size: '38', fits: '40D/38DD(E)' }, 'D/E/F': { size: '38', fits: '40D/38DD(E)/36DDD(F)' } },
        'DD(E)': { 'CDDD': { size: '42', fits: '44C/42D/40DD(E)' }, 'CDDD-pam': { size: '40', fits: '44C/42D/40DD(E)' }, 'D/DD': { size: '40', fits: '42D/40DD(E)' }, 'DD/E/F': { size: '40', fits: '40DD(E)/38DDD(F)' }, 'D/E/F': { size: '40', fits: '42D/40DD(E)/38DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '42', fits: '42DD(E)/40DDD(F)' }, 'D/E/F': { size: '42', fits: '44D/42DD(E)/40DDD(F)' } }
      },
      '42': {
        'A': {},
        'B': {},
        'C': { 'CDDD': { size: '40', fits: '42C/40D/38DD(E)' }, 'CDDD-pam': { size: '38', fits: '42C/40D/38DD(E)' } },
        'D': { 'CDDD': { size: '42', fits: '44C/42D/40DD(E)' }, 'CDDD-pam': { size: '40', fits: '44C/42D/40DD(E)' }, 'D/DD': { size: '40', fits: '42D/40DD(E)' }, 'D/E/F': { size: '40', fits: '42D/40DD(E)/38DDD(F)' } },
        'DD(E)': { 'CDDD': { size: '44', fits: '46C/44D/42DD(E)' }, 'D/DD': { size: '42', fits: '44D/42DD(E)' }, 'DD/E/F': { size: '42', fits: '42DD(E)/40DDD(F)' }, 'D/E/F': { size: '42', fits: '44D/42DD(E)/40DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '44', fits: '44DD(E)/42DDD(F)' }, 'D/E/F': { size: '44', fits: '46D/44DD(E)/42DDD(F)' } }
      },
      '44': {
        'A': {},
        'B': {},
        'C': { 'CDDD': { size: '42', fits: '44C/42D/40DD(E)' }, 'CDDD-pam': { size: '40', fits: '44C/42D/40DD(E)' } },
        'D': { 'CDDD': { size: '44', fits: '46C/44D/42DD(E)' }, 'D/E/F': { size: '42', fits: '44D/42DD(E)/40DDD(F)' } },
        'DD(E)': { 'D/DD': { size: '44', fits: '46D/44DD(E)' }, 'DD/E/F': { size: '44', fits: '44DD(E)/42DDD(F)' }, 'D/E/F': { size: '44', fits: '46D/44DD(E)/42DDD(F)' } },
        'DDD(F)': {}
      },
      '46': {
        'A': {},
        'B': {},
        'C': { 'CDDD': { size: '44', fits: '46C/44D/42DD(E)' } },
        'D': { 'D/DD': { size: '44', fits: '46D/44DD(E)' }, 'D/E/F': { size: '44', fits: '46D/44DD(E)/42DDD(F)' } },
        'DD(E)': {},
        'DDD(F)': {}
      }
    };

    // Matrix for maillots
    this.sizeMatrixMaillots = {
      'A': {
        '1': { 'ABCD': 1, 'AB': 1, 'ABC': 1, 'BC': 1 },
        '2': { 'ABCD': 2, 'AB': 2, 'ABC': 2, 'BC': 2 },
        '4': { 'ABCD': 4, 'AB': 4, 'ABC': 4, 'BC': 4 },
        '6': { 'ABCD': 6, 'AB': 6, 'ABC': 6, 'BC': 6 },
        '8': { 'ABCD': 8, 'AB': 8, 'ABC': 8, 'BC': 8 },
        '10': { 'ABCD': 10, 'AB': 10, 'ABC': 10, 'BC': 10 },
        '12': { 'ABCD': 12, 'AB': 12, 'ABC': 12, 'BC': 12 },
        '14': { 'ABCD': 14, 'AB': 14, 'ABC': 14, 'BC': 14 },
        '16': { 'ABCD': 16, 'AB': 16, 'ABC': 16, 'BC': 16 }
      },
      'B': {
        '1': { 'ABCD': 1, 'AB': 1, 'ABC': 1, 'BC': 1 },
        '2': { 'ABCD': 2, 'AB': 2, 'ABC': 2, 'BC': 2 },
        '4': { 'ABCD': 4, 'AB': 4, 'ABC': 4, 'BC': 4 },
        '6': { 'ABCD': 6, 'AB': 6, 'ABC': 6, 'BC': 6 },
        '8': { 'ABCD': 8, 'AB': 8, 'ABC': 8, 'BC': 8 },
        '10': { 'ABCD': 10, 'AB': 10, 'ABC': 10, 'BC': 10 },
        '12': { 'ABCD': 12, 'AB': 12, 'ABC': 12, 'BC': 12 },
        '14': { 'ABCD': 14, 'AB': 14, 'ABC': 14, 'BC': 14 },
        '16': { 'ABCD': 16, 'AB': 16, 'ABC': 16, 'BC': 16 }
      },
      'C': {
        '1': { 'ABCD': 1, 'ABC': 1, 'BC': 1, 'CD': 1, 'CDDD': 1, 'CDDD-pam': 1 },
        '2': { 'ABCD': 2, 'ABC': 2, 'BC': 2, 'CD': 2, 'CDDD': 2, 'CDDD-pam': 2 },
        '4': { 'ABCD': 4, 'ABC': 4, 'BC': 4, 'CD': 4, 'CDDD': 4, 'CDDD-pam': 4 },
        '6': { 'ABCD': 6, 'ABC': 6, 'BC': 6, 'CD': 6, 'CDDD': 6, 'CDDD-pam': 6 },
        '8': { 'ABCD': 8, 'ABC': 8, 'BC': 8, 'CD': 8, 'CDDD': 8, 'CDDD-pam': 8 },
        '10': { 'ABCD': 10, 'ABC': 10, 'BC': 10, 'CD': 10, 'CDDD': 10, 'CDDD-pam': 10 },
        '12': { 'ABCD': 12, 'ABC': 12, 'BC': 12, 'CD': 12, 'CDDD': 12, 'CDDD-pam': 12 },
        '14': { 'ABCD': 14, 'ABC': 14, 'BC': 14, 'CD': 14, 'CDDD': 14, 'CDDD-pam': 14 },
        '16': { 'ABCD': 16, 'ABC': 16, 'BC': 16, 'CD': 16, 'CDDD': 16, 'CDDD-pam': 16 }
      },
      'D': {
        '1': { 'CD': 1, 'CDDD': 1, 'CDDD-pam': 1, 'D/DD': 1, 'DD/E/F': 1, 'D/E/F': 1 },
        '2': { 'CD': 2, 'CDDD': 2, 'CDDD-pam': 2, 'D/DD': 2, 'DD/E/F': 2, 'D/E/F': 2 },
        '4': { 'CD': 4, 'CDDD': 4, 'CDDD-pam': 4, 'D/DD': 4, 'DD/E/F': 4, 'D/E/F': 4 },
        '6': { 'CD': 6, 'CDDD': 6, 'CDDD-pam': 6, 'D/DD': 6, 'DD/E/F': 6, 'D/E/F': 6 },
        '8': { 'CD': 8, 'CDDD': 8, 'CDDD-pam': 8, 'D/DD': 8, 'DD/E/F': 8, 'D/E/F': 8 },
        '10': { 'CD': 10, 'CDDD': 10, 'CDDD-pam': 10, 'D/DD': 10, 'DD/E/F': 10, 'D/E/F': 10 },
        '12': { 'CD': 12, 'CDDD': 12, 'CDDD-pam': 12, 'D/DD': 12, 'DD/E/F': 12, 'D/E/F': 12 },
        '14': { 'CD': 14, 'CDDD': 14, 'CDDD-pam': 14, 'D/DD': 14, 'DD/E/F': 14, 'D/E/F': 14 },
        '16': { 'CD': 16, 'CDDD': 16, 'CDDD-pam': 16, 'D/DD': 16, 'DD/E/F': 16, 'D/E/F': 16 }
      },
      'E': {
        '1': { 'D/DD': 1, 'DD/E/F': 1, 'D/E/F': 1 },
        '2': { 'D/DD': 2, 'DD/E/F': 2, 'D/E/F': 2 },
        '4': { 'D/DD': 4, 'DD/E/F': 4, 'D/E/F': 4 },
        '6': { 'D/DD': 6, 'DD/E/F': 6, 'D/E/F': 6 },
        '8': { 'D/DD': 8, 'DD/E/F': 8, 'D/E/F': 8 },
        '10': { 'D/DD': 10, 'DD/E/F': 10, 'D/E/F': 10 },
        '12': { 'D/DD': 12, 'DD/E/F': 12, 'D/E/F': 12 },
        '14': { 'D/DD': 14, 'DD/E/F': 14, 'D/E/F': 14 },
        '16': { 'D/DD': 16, 'DD/E/F': 16, 'D/E/F': 16 }
      },
      'F': {
        '1': { 'DD/E/F': 1, 'D/E/F': 1 },
        '2': { 'DD/E/F': 2, 'D/E/F': 2 },
        '4': { 'DD/E/F': 4, 'D/E/F': 4 },
        '6': { 'DD/E/F': 6, 'D/E/F': 6 },
        '8': { 'DD/E/F': 8, 'D/E/F': 8 },
        '10': { 'DD/E/F': 10, 'D/E/F': 10 },
        '12': { 'DD/E/F': 12, 'D/E/F': 12 },
        '14': { 'DD/E/F': 14, 'D/E/F': 14 },
        '16': { 'DD/E/F': 16, 'D/E/F': 16 }
      }
    };

    this.bandSizeDropdown = null;
    this.cupSizeDropdown = null;
    this.pantSizeDropdown = null;
    this.heightDropdown = null;
    this.resultMessage = null;
    this.toggleButton = null;
    this.toggleContent = null;
    this.scrollPinTimer = null;

    this.init();
}

getProductType() {
    const productJsonScript = document.querySelector('[data-product-json]');
    if (!productJsonScript) return 'tops';
    
    try {
      const product = JSON.parse(productJsonScript.textContent);
      const type = product.type || 'swimwear tops';
      
      if (type.toLowerCase().includes('maillot')) {
        return 'maillots';
      }
      return 'tops';
    } catch (e) {
      console.error('Error parsing product type:', e);
      return 'tops';
    }
  }

  init() {
    this.render();
    this.setupEventListeners();
    this.resultMessage.classList.add('disabled');
  }

  render() {
    const productJsonScript = document.querySelector('[data-product-json]');
    let productName = 'THIS STYLE';
    if (productJsonScript) {
        try {
        const product = JSON.parse(productJsonScript.textContent);
        productName = (product.title || 'THIS STYLE').toUpperCase();
        } catch (e) {
        console.error('Error parsing product JSON:', e);
        }
    }

    let gridClass = this.productType === 'maillots' ? ' maillot' : '';
    let html;

    if (this.productType === 'maillots') {
        html = `
        <div class="bra-size-selector">
            <button class="bra-size-selector__toggle" type="button">
            <span>FIND MY SIZE</span>
            <span class="bra-size-selector__toggle-icon">▼</span>
            </button>

            <div class="bra-size-selector__content">
            <div class="bra-size-selector__grid${gridClass}">
                <div class="size-selector-group">
                <label for="cup-size">CUP SIZE<button type="button" class="cup-size-help-link" aria-haspopup="dialog" aria-label="How to measure your cup size">&#x24D8;</button></label>
                <select id="cup-size" class="cup-size-dropdown">
                    <option value="">—</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                    <option value="DD(E)">DD(E)</option>
                    <option value="DDD(F)">DDD(F)</option>
                </select>
                </div>

                <div class="size-selector-group">
                <label for="pant-size"><span>PANT SIZE<button type="button" class="pant-size-help-link" aria-haspopup="dialog" aria-label="How to measure your pant size">&#x24D8;</button></label>
                <select id="pant-size" class="pant-size-dropdown" disabled>
                    <option value="">—</option>
                </select>
                </div>

                <div class="size-selector-group">
                <label for="height">HEIGHT</label>
                <select id="height" class="height-dropdown" disabled>
                    <option value="">—</option>
                    <option value="P">UNDER 5'2"</option>
                    <option value="R">5'2" - 5'8"</option>
                    <option value="T">OVER 5'8"</option>
                </select>
                </div>

                <div class="size-result">
                <label class="size-result__label">OUR PICK:</label>
                <p class="result-message">—</p>
                </div>
            </div>
          </div>
        </div>

        <!-- Cup Size Measurement Modal -->
        <div class="band-size-modal-overlay" id="cupSizeModal" role="dialog" aria-modal="true" aria-labelledby="cupSizeModalTitle" hidden>
          <div class="band-size-modal">
            <button type="button" class="band-size-modal__close" aria-label="Close">&#x2715;</button>
            <h2 class="band-size-modal__title" id="cupSizeModalTitle">HOW TO MEASURE YOUR CUP SIZE</h2>

            <div class="band-size-modal__body">

              <!-- Image column -->
              <div class="band-size-modal__left">
                <img
                  src="https://cdn.shopify.com/s/files/1/1141/8998/files/Size_Chart_Top_2_Dots.jpg?v=1773332838"
                  alt="Measure across your fullest point, from the center of your chest to the other side"
                  width="400"
                  height="500"
                  loading="lazy"
                />
              </div>

              <!-- Chart column -->
              <div class="band-size-modal__chart">
                <p class="band-size-modal__caption band-size-modal__caption">Wear an unlined, unpadded bra & measure your fullest breast. Start with the measuring tape in the center of your chest. Measure across one breast to the other side at the fullest point.</p>
                <table>
                  <thead>
                    <tr>
                      <th>MEASUREMENT</th>
                      <th>CUP SIZE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>7"</td><td>A</td></tr>
                    <tr><td>8"</td><td>B</td></tr>
                    <tr><td>9"</td><td>C</td></tr>
                    <tr><td>10"</td><td>D</td></tr>
                    <tr><td>11"</td><td>DD(E)</td></tr>
                    <tr><td>12"</td><td>DDD(F)</td></tr>
                  </tbody>
                </table>
                <p class="band-size-modal__caption band-size-modal__caption--italic">Need help? Chat with Christine in the bottom-right or call one of <a href="https://www.maliamills.com/pages/our-stores">our stores</a> - we're pros!</p>
              </div>
            </div><!-- /.band-size-modal__body -->
          </div><!-- /.band-size-modal -->
        </div><!-- /.band-size-modal-overlay -->

        <!-- Pant Size Measurement Modal -->
        <div class="band-size-modal-overlay" id="pantSizeModal" role="dialog" aria-modal="true" aria-labelledby="pantSizeModalTitle" hidden>
          <div class="band-size-modal">
            <button type="button" class="band-size-modal__close" aria-label="Close">&#x2715;</button>
            <h2 class="band-size-modal__title" id="pantSizeModalTitle">HOW TO MEASURE YOUR PANT SIZE</h2>

            <div class="band-size-modal__body">

              <!-- Image column -->
              <div class="band-size-modal__left">
                <img
                  src="https://cdn.shopify.com/s/files/1/1141/8998/files/Size_Chart_Maillot_2_Dots.jpg?v=1773335426"
                  alt="Without clothing, measure your waist at the narrowest point and your hip at the fullest point"
                  width="400"
                  height="500"
                  loading="lazy"
                />
              </div>

              <!-- Chart column -->
              <div class="band-size-modal__chart">
              <p class="band-size-modal__caption band-size-modal__caption--desktop">Without clothing, measure your waist (the narrowest part of your torso) & straight across the fullest point of your hip.</p>
                <table>
                  <thead>
                    <tr>
                      <th>WAIST</th>
                      <th>HIP</th>
                      <th>SIZE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>23.5" - 24.5"</td><td>34.5" - 35.5"</td><td>1</td></tr>
                    <tr><td>25" - 26"</td><td>36" - 37"</td><td>2</td></tr>
                    <tr><td>26.5" - 27.5"</td><td>37.5" - 38.5"</td><td>4</td></tr>
                    <tr><td>28" - 29"</td><td>39" - 40"</td><td>6</td></tr>
                    <tr><td>29.5" - 30.5"</td><td>40.5" - 41.5"</td><td>8</td></tr>
                    <tr><td>31" - 32"</td><td>42" - 43"</td><td>10</td></tr>
                    <tr><td>32.5" - 34"</td><td>43.5" - 44.5"</td><td>12</td></tr>
                    <tr><td>35" - 37"</td><td>45.5" - 47"</td><td>14</td></tr>
                    <tr><td>37.5" - 39"</td><td>47.5" - 49"</td><td>16</td></tr>
                    <tr><td>39.5" - 41"</td><td>49.5" - 51"</td><td>18</td></tr>
                    <tr><td>41.5" - 43"</td><td>51.5" - 53"</td><td>20</td></tr>
                    <tr><td>43.5" - 45"</td><td>53.5" - 55"</td><td>22</td></tr>
                  </tbody>
                </table>
                <p class="band-size-modal__caption band-size-modal__caption--italic">Need help? Chat with Christine in the bottom-right or call one of <a href="https://www.maliamills.com/pages/our-stores">our stores</a> - we're pros!</p>
              </div>
            </div><!-- /.band-size-modal__body -->
          </div><!-- /.band-size-modal -->
        </div><!-- /.band-size-modal-overlay -->
        
        `;
    } else {
        html = `
        <div class="bra-size-selector">
            <button class="bra-size-selector__toggle" type="button">
            <span>FIND MY SIZE</span>
            <span class="bra-size-selector__toggle-icon">▼</span>
            </button>

            <div class="bra-size-selector__content">
            <div class="bra-size-selector__grid${gridClass}">
                <div class="size-selector-group">
                <label for="band-size">BAND SIZE<button type="button" class="band-size-help-link" aria-haspopup="dialog" aria-label="How to measure your band size">&#x24D8;</button></label>
                <select id="band-size" class="band-size-dropdown">
                    <option value="">—</option>
                    <option value="28">28</option>
                    <option value="30">30</option>
                    <option value="32">32</option>
                    <option value="34">34</option>
                    <option value="36">36</option>
                    <option value="38">38</option>
                    <option value="40">40</option>
                    <option value="42">42</option>
                    <option value="44">44</option>
                    <option value="46">46</option>
                </select>
                </div>

                <div class="size-selector-group">
                <label for="cup-size">CUP SIZE<button type="button" class="cup-size-help-link" aria-haspopup="dialog" aria-label="How to measure your cup size">&#x24D8;</button></label>
                <select id="cup-size" class="cup-size-dropdown" disabled>
                    <option value="">—</option>
                </select>
                </div>

                <div class="size-result">
                <label class="size-result__label">OUR PICK: ${productName}</label>
                <p class="result-message">—</p>
                </div>
            </div>
          </div>
        </div>

        <!-- Band Size Measurement Modal -->
          <div class="band-size-modal-overlay" id="bandSizeModal" role="dialog" aria-modal="true" aria-labelledby="bandSizeModalTitle" hidden>
            <div class="band-size-modal">
              <button type="button" class="band-size-modal__close" aria-label="Close">&#x2715;</button>
              <h2 class="band-size-modal__title" id="bandSizeModalTitle">HOW TO MEASURE YOUR BAND SIZE</h2>

              <div class="band-size-modal__body">

                <!-- Image column -->
                <div class="band-size-modal__left">
                  <img
                    src="https://cdn.shopify.com/s/files/1/1141/8998/files/Size_Chart_Top_1_Full.jpg?v=1773330781"
                    alt="Wrap a measuring tape snugly around your ribcage, just under your bust"
                    width="400"
                    height="500"
                    loading="lazy"
                  />
                  <p class="band-size-modal__caption band-size-modal__caption--mobile">Measure around your rib cage, pulling measuring tape as snug as your favorite bra fits. Round to the nearest whole number.</p>
                </div>
        
                <!-- Chart column -->
                <div class="band-size-modal__chart">
                  <p class="band-size-modal__caption band-size-modal__caption--desktop">Measure around your rib cage, pulling measuring tape as snug as your favorite bra fits. Round to the nearest whole number.</p>
                  <table>
                    <thead>
                      <tr>
                        <th>MEASUREMENT</th>
                        <th>BAND SIZE</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>25" – 26"</td><td>30</td></tr>
                      <tr><td>27" – 28"</td><td>32</td></tr>
                      <tr><td>29" – 30"</td><td>34</td></tr>
                      <tr><td>31" – 32"</td><td>36</td></tr>
                      <tr><td>33" – 34"</td><td>38</td></tr>
                      <tr><td>35" – 36"</td><td>40</td></tr>
                      <tr><td>37" – 38"</td><td>42</td></tr>
                      <tr><td>39" – 40"</td><td>44</td></tr>
                    </tbody>
                  </table>
                  <p class="band-size-modal__caption band-size-modal__caption--italic">Need help? Chat with Christine in the bottom-right or call one of <a href="https://www.maliamills.com/pages/our-stores">our stores</a> - we're pros!</p>
                </div>
  
              </div><!-- /.band-size-modal__body -->
            </div><!-- /.band-size-modal -->
          </div><!-- /.band-size-modal-overlay -->

        <!-- Cup Size Measurement Modal -->
        <div class="band-size-modal-overlay" id="cupSizeModal" role="dialog" aria-modal="true" aria-labelledby="cupSizeModalTitle" hidden>
          <div class="band-size-modal">
            <button type="button" class="band-size-modal__close" aria-label="Close">&#x2715;</button>
            <h2 class="band-size-modal__title" id="cupSizeModalTitle">HOW TO MEASURE YOUR CUP SIZE</h2>

            <div class="band-size-modal__body">

              <!-- Image column -->
              <div class="band-size-modal__left">
                <img
                  src="https://cdn.shopify.com/s/files/1/1141/8998/files/Size_Chart_Top_2_Dots.jpg?v=1773332838"
                  alt="Measure across your fullest point, from the center of your chest to the other side"
                  width="400"
                  height="500"
                  loading="lazy"
                />
                <p class="band-size-modal__caption band-size-modal__caption--mobile">Wear an unlined, unpadded bra & measure your fullest breast. Start with the measuring tape in the center of your chest. Measure across one breast to the other side at the fullest point.</p>
              </div>

              <!-- Chart column -->
              <div class="band-size-modal__chart">
                <p class="band-size-modal__caption band-size-modal__caption--desktop">Wear an unlined, unpadded bra & measure your fullest breast. Start with the measuring tape in the center of your chest. Measure across one breast to the other side at the fullest point.</p>
                <table>
                  <thead>
                    <tr>
                      <th>MEASUREMENT</th>
                      <th>CUP SIZE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>7"</td><td>A</td></tr>
                    <tr><td>8"</td><td>B</td></tr>
                    <tr><td>9"</td><td>C</td></tr>
                    <tr><td>10"</td><td>D</td></tr>
                    <tr><td>11"</td><td>DD(E)</td></tr>
                    <tr><td>12"</td><td>DDD(F)</td></tr>
                  </tbody>
                </table>
                <p class="band-size-modal__caption band-size-modal__caption--italic">Need help? Chat with Christine in the bottom-right or call one of <a href="https://www.maliamills.com/pages/our-stores">our stores</a> - we're pros!</p>
              </div>

            </div><!-- /.band-size-modal__body -->
          </div><!-- /.band-size-modal -->
        </div><!-- /.band-size-modal-overlay -->

        `;
    }
    this.container.innerHTML = html;

    this.toggleButton = this.container.querySelector('.bra-size-selector__toggle');
    this.toggleContent = this.container.querySelector('.bra-size-selector__content');
    this.resultMessage = this.container.querySelector('.result-message');

    if (this.productType === 'maillots') {
        this.cupSizeDropdown = this.container.querySelector('.cup-size-dropdown');
        this.pantSizeDropdown = this.container.querySelector('.pant-size-dropdown');
        this.heightDropdown = this.container.querySelector('.height-dropdown');
    } else {
        this.bandSizeDropdown = this.container.querySelector('.band-size-dropdown');
        this.cupSizeDropdown = this.container.querySelector('.cup-size-dropdown');
    }
    }

  setupEventListeners() {
    this.toggleButton.addEventListener('click', () => this.toggleCollapsible());

    if (this.productType === 'maillots') {
      this.cupSizeDropdown.addEventListener('change', () => this.handleMaillotCupSizeChange());
      this.pantSizeDropdown.addEventListener('change', () => this.handleMaillotChange());
      this.heightDropdown.addEventListener('change', () => this.handleMaillotChange());

      // Cup size modal for maillots
      const cupHelpLink = this.container.querySelector('.cup-size-help-link');
      const cupModal = this.container.querySelector('#cupSizeModal');
      const cupCloseBtn = cupModal ? cupModal.querySelector('.band-size-modal__close') : null;

      if (cupHelpLink && cupModal) {
        cupHelpLink.addEventListener('click', () => this.openModal(cupModal, cupHelpLink));

        if (cupCloseBtn) {
          cupCloseBtn.addEventListener('click', () => this.closeModal(cupModal, cupHelpLink));
        }

        cupModal.addEventListener('click', (e) => {
          if (e.target === cupModal) this.closeModal(cupModal, cupHelpLink);
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !cupModal.hidden) this.closeModal(cupModal, cupHelpLink);
        });
      }

      // Pant size modal for maillots
      const pantHelpLink = this.container.querySelector('.pant-size-help-link');
      const pantModal = this.container.querySelector('#pantSizeModal');
      const pantCloseBtn = pantModal ? pantModal.querySelector('.band-size-modal__close') : null;

      if (pantHelpLink && pantModal) {
        pantHelpLink.addEventListener('click', () => this.openModal(pantModal, pantHelpLink));

        if (pantCloseBtn) {
          pantCloseBtn.addEventListener('click', () => this.closeModal(pantModal, pantHelpLink));
        }

        pantModal.addEventListener('click', (e) => {
          if (e.target === pantModal) this.closeModal(pantModal, pantHelpLink);
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !pantModal.hidden) this.closeModal(pantModal, pantHelpLink);
        });
      }

    } else {
      this.bandSizeDropdown.addEventListener('change', () => this.handleBandSizeChange());
      this.cupSizeDropdown.addEventListener('change', () => this.handleCupSizeChange());
 
      // Modal open/close
      const helpLink = this.container.querySelector('.band-size-help-link');
      const modal = this.container.querySelector('#bandSizeModal');
      const closeBtn = modal ? modal.querySelector('.band-size-modal__close') : null;
 
      if (helpLink && modal) {
        helpLink.addEventListener('click', () => this.openModal(modal, helpLink));
 
        if (closeBtn) {
          closeBtn.addEventListener('click', () => this.closeModal(modal, helpLink));
        }
 
        // Close on overlay backdrop click
        modal.addEventListener('click', (e) => {
          if (e.target === modal) this.closeModal(modal, helpLink);
        });
 
        // Close on Escape key
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !modal.hidden) this.closeModal(modal, helpLink);
        });
      }

      const cupHelpLink = this.container.querySelector('.cup-size-help-link');
      const cupModal = this.container.querySelector('#cupSizeModal');
      const cupCloseBtn = cupModal ? cupModal.querySelector('.band-size-modal__close') : null;

      if (cupHelpLink && cupModal) {
        cupHelpLink.addEventListener('click', () => this.openModal(cupModal, cupHelpLink));

        if (cupCloseBtn) {
          cupCloseBtn.addEventListener('click', () => this.closeModal(cupModal, cupHelpLink));
        }

        cupModal.addEventListener('click', (e) => {
          if (e.target === cupModal) this.closeModal(cupModal, cupHelpLink);
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !cupModal.hidden) this.closeModal(cupModal, cupHelpLink);
        });
      }
    }
  }
 
  openModal(modal, triggerEl) {
    modal.hidden = false;
    // Get scrollbar width
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = scrollbarWidth + 'px';
    // Move focus into modal for accessibility
    const closeBtn = modal.querySelector('.band-size-modal__close');
    if (closeBtn) closeBtn.focus();
  }
 
  closeModal(modal, triggerEl) {
    modal.hidden = true;
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';  /* ← Reset the padding */
    // Return focus to trigger
    if (triggerEl) triggerEl.focus();
  }

  toggleCollapsible() {
    const scrollY = window.scrollY;

    this.toggleButton.classList.toggle('is-open');
    this.toggleContent.classList.toggle('is-open');

    // Some browsers try to scroll the toggle/its expanding content into
    // view once the layout changes — pin the scroll position so the page
    // stays put and the revealed content just extends past the viewport.
    window.scrollTo(window.scrollX, scrollY);
    requestAnimationFrame(() => window.scrollTo(window.scrollX, scrollY));
    clearTimeout(this.scrollPinTimer);
    this.scrollPinTimer = setTimeout(() => window.scrollTo(window.scrollX, scrollY), 320);
  }


  // TOPS METHODS
  handleBandSizeChange() {
    const bandSize = this.bandSizeDropdown.value;

    this.cupSizeDropdown.value = '';
    this.cupSizeDropdown.innerHTML = '<option value="">—</option>';
    this.cupSizeDropdown.disabled = !bandSize;
    
    this.resultMessage.textContent = '—';
    this.resultMessage.classList.add('disabled');

    if (!bandSize) return;

    const validCupSizes = Object.keys(this.sizeMatrixTops[bandSize]).filter(
      cupSize => Object.keys(this.sizeMatrixTops[bandSize][cupSize]).length > 0
    );

    validCupSizes.forEach(cupSize => {
      const option = document.createElement('option');
      option.value = cupSize;
      option.textContent = cupSize;
      this.cupSizeDropdown.appendChild(option);
    });
  }

  handleCupSizeChange() {
    const bandSize = this.bandSizeDropdown.value;
    const cupSize = this.cupSizeDropdown.value;

    if (!bandSize || !cupSize) {
      this.resultMessage.textContent = '—';
      this.resultMessage.classList.add('disabled');
      return;
    }

    this.resultMessage.classList.remove('disabled');

    const variant = this.findMatchingVariantTops(bandSize, cupSize);

    if (variant) {
      this.showResult(variant);
      this.appendCUpgradeSuggestion(cupSize);
    } else {
      const companion = this.findCompanionProduct(cupSize);
      if (companion) {
        this.showCompanionSuggestion(companion);
      } else {
        this.showError();
      }
    }
  }

  // MAILLOT METHODS
  handleMaillotCupSizeChange() {
    const cupSize = this.cupSizeDropdown.value;

    this.pantSizeDropdown.value = '';
    this.pantSizeDropdown.innerHTML = '<option value="">—</option>';
    this.pantSizeDropdown.disabled = !cupSize;
    
    this.heightDropdown.value = '';
    this.heightDropdown.disabled = true;
    
    this.resultMessage.textContent = '—';
    this.resultMessage.classList.add('disabled');

    if (!cupSize) return;

    // Check if this cup size has any valid options for this product's cup type
    const matrixForCupSize = this.sizeMatrixMaillots[cupSize] || {};
    
    // Get the product's cup type(s)
    const braMetaScript = document.querySelector('script[id^="BraSizeMetafields"]');
    let productCupTypes = new Set();
    if (braMetaScript) {
        try {
        const metafields = JSON.parse(braMetaScript.textContent);
        metafields.forEach(m => {
            if (m.cup_size) productCupTypes.add(m.cup_size);
        });
        } catch (e) {
        console.error('Error parsing metafields:', e);
        }
    }

    // Check if any pant size has a matching cup type for this product
    let hasMatch = false;
    for (const pantSize of Object.keys(matrixForCupSize)) {
        const cupTypeOptions = matrixForCupSize[pantSize];
        for (const cupType of Object.keys(cupTypeOptions)) {
        if (productCupTypes.has(cupType)) {
            hasMatch = true;
            break;
        }
        }
        if (hasMatch) break;
    }

    // If no match found for this product's cup types, show error
    if (!hasMatch) {
        this.showError();
        this.pantSizeDropdown.disabled = true;
        return;
    }

    // Otherwise populate pant sizes
    const validPantSizes = Object.keys(matrixForCupSize);
    validPantSizes.forEach(pantSize => {
        const option = document.createElement('option');
        option.value = pantSize;
        option.textContent = pantSize;
        this.pantSizeDropdown.appendChild(option);
    });
    }

  handleMaillotChange() {
    const cupSize = this.cupSizeDropdown.value;
    const pantSize = this.pantSizeDropdown.value;
    const height = this.heightDropdown.value;

    this.heightDropdown.disabled = !pantSize;

    if (!cupSize || !pantSize || !height) {
      this.resultMessage.textContent = '—';
      this.resultMessage.classList.add('disabled');
      return;
    }

    this.resultMessage.classList.remove('disabled');

    const variant = this.findMatchingVariantMaillot(cupSize, pantSize, height);

    if (variant) {
      this.showResult(variant);
    } else {
      this.showError();
    }
  }

  // VARIANT MATCHING
  findMatchingVariantTops(bandSize, cupSize) {
    const cupTypeOptions = this.sizeMatrixTops[bandSize][cupSize];
    
    const productJsonScript = document.querySelector('[data-product-json]');
    if (!productJsonScript) return null;
    
    const product = JSON.parse(productJsonScript.textContent);
    const variants = product.variants || [];

    const braMetaScript = document.querySelector('script[id^="BraSizeMetafields"]');
    let metafieldMap = {};
    if (braMetaScript) {
      try {
        const metafields = JSON.parse(braMetaScript.textContent);
        metafieldMap = Object.fromEntries(metafields.map(m => [m.variant_id.toString(), m.cup_size]));
      } catch (e) {
        console.error('Error parsing bra size metafields:', e);
      }
    }

    for (const [cupType, variantInfo] of Object.entries(cupTypeOptions)) {
      const matchingVariant = variants.find(variant => {
        const sizeMatch = variant.title.includes(variantInfo.size);
        const variantCupType = metafieldMap[variant.id.toString()];
        const cupTypeMatch = variantCupType === cupType;

        return sizeMatch && cupTypeMatch;
      });

      if (matchingVariant) {
        return {
          variant: matchingVariant,
          fits: variantInfo.fits,
          size: variantInfo.size,
          cupType: cupType
        };
      }
    }

    return null;
  }

  findMatchingVariantMaillot(cupSize, pantSize, height) {
    const baseSize = this.sizeMatrixMaillots[cupSize]?.[pantSize];
    if (!baseSize) return null;

    const braMetaScript = document.querySelector('script[id^="BraSizeMetafields"]');
    let metafieldMap = {};
    if (braMetaScript) {
      try {
        const metafields = JSON.parse(braMetaScript.textContent);
        metafieldMap = Object.fromEntries(metafields.map(m => [m.variant_id.toString(), m.cup_size]));
      } catch (e) {
        console.error('Error parsing metafields:', e);
      }
    }

    const productJsonScript = document.querySelector('[data-product-json]');
    if (!productJsonScript) return null;
    
    const product = JSON.parse(productJsonScript.textContent);
    const variants = product.variants || [];

    // Adjust size based on height
    let targetSize = baseSize;
    if (height === 'P') {
      targetSize = this.adjustSize(baseSize, -1);
    } else if (height === 'T') {
      targetSize = this.adjustSize(baseSize, 1);
    }

    // Find matching variant with target size
    for (const [cupType, size] of Object.entries(baseSize)) {
      const adjustedSize = height === 'P' 
        ? this.adjustSize(size, -1) 
        : height === 'T' 
        ? this.adjustSize(size, 1) 
        : size;

      const matchingVariant = variants.find(variant => {
        const sizeMatch = variant.title.includes(adjustedSize.toString());
        const variantCupType = metafieldMap[variant.id.toString()];
        const cupTypeMatch = variantCupType === cupType;

        return sizeMatch && cupTypeMatch;
      });

      if (matchingVariant) {
        return {
          variant: matchingVariant,
          size: adjustedSize,
          fits: `${adjustedSize}`
        };
      }
    }

    return null;
  }

  adjustSize(size, adjustment) {
    const sizeMap = {
      1: 2, 2: 4, 4: 6, 6: 8, 8: 10, 10: 12, 12: 14, 14: 16, 16: 18
    };
    const reverseMap = {
      2: 1, 4: 2, 6: 4, 8: 6, 10: 8, 12: 10, 14: 12, 16: 14, 18: 16
    };

    if (adjustment === 1) {
      return sizeMap[size] || size;
    } else if (adjustment === -1) {
      return reverseMap[size] || size;
    }
    return size;
  }

  getSelectedColor() {
    // Dawn/Debut themes use radio inputs with data-index="option1" for color
    const colorInput = document.querySelector('input[type="radio"][data-index="option1"]:checked');
    if (colorInput) return colorInput.value.toLowerCase();

    // Fallback: look for a color swatch variant selector
    const colorSelect = document.querySelector('.product-form__input--swatch input:checked, .variant-input--color input:checked');
    if (colorSelect) return colorSelect.value.toLowerCase();

    return null;
  }

  stepUpBandSize(size) {
    const steps = ['28','30','32','34','36','38','40','42','44','46'];
    const idx = steps.indexOf(String(size));
    return idx !== -1 && idx < steps.length - 1 ? steps[idx + 1] : String(size);
  }

  showResult(result) {
    // Check if this product has per-color overrides
    const productJsonScript = document.querySelector('[data-product-json]');
    let overrideConfig = null;
    if (productJsonScript) {
      try {
        const product = JSON.parse(productJsonScript.textContent);
        const titleLower = (product.title || '').toLowerCase();
        for (const [key, config] of Object.entries(this.productOverrides)) {
          if (titleLower.includes(key)) {
            overrideConfig = config;
            break;
          }
        }
      } catch (e) { /* ignore */ }
    }

    if (overrideConfig && this.productType === 'tops') {
      const adjustedBandSize = this.stepUpBandSize(result.size);
      const cupSize = this.cupSizeDropdown.value;
      const bandSize = this.bandSizeDropdown.value;

      if (overrideConfig.adjustedOnly) {
        let adjustedFits = result.fits;
        if (this.sizeMatrixTops[adjustedBandSize]?.[cupSize]?.[result.cupType]) {
          adjustedFits = this.sizeMatrixTops[adjustedBandSize][cupSize][result.cupType].fits;
        }
        this.resultMessage.innerHTML = `<strong>SIZE ${adjustedBandSize}</strong>&nbsp;(FITS ${adjustedFits})`;
        this.resultMessage.classList.remove('error', 'has-upgrade');
        this.selectSizeVariant(adjustedBandSize);
      } else {
        let adjustedFits = result.fits;
        if (this.sizeMatrixTops[adjustedBandSize] && this.sizeMatrixTops[adjustedBandSize][cupSize]) {
          const cupTypeOptions = this.sizeMatrixTops[adjustedBandSize][cupSize];
          const firstEntry = Object.values(cupTypeOptions)[0];
          if (firstEntry) adjustedFits = firstEntry.fits;
        }

        const splitText = `${overrideConfig.defaultLabel}: <strong>SIZE ${result.size}</strong>\n${overrideConfig.adjustedLabel}: <strong>SIZE ${adjustedBandSize}</strong>\n(FITS ${result.fits})`;

        this.resultMessage.innerHTML = `<span style="white-space: pre-line">${splitText}</span>`;
        this.resultMessage.classList.remove('error', 'has-upgrade');

        const selectedColor = this.getSelectedColor();
        if (selectedColor && overrideConfig.adjustedColors.some(c => selectedColor.includes(c))) {
          this.selectSizeVariant(adjustedBandSize);
        } else {
          this.selectSizeVariant(result.size);
        }
      }

      this.createOrUpdateInput('properties[Top Band Size]', bandSize);
      this.createOrUpdateInput('properties[Top Cup Size]', cupSize);

    } else {
      // Original behavior
      let resultText;
      if (this.productType === 'maillots') {
        resultText = `<strong>SIZE ${result.size}</strong>`;
      } else {
        resultText = `<strong>SIZE ${result.size}</strong>&nbsp;(FITS ${result.fits})`;
      }

      this.resultMessage.innerHTML = resultText;
      this.resultMessage.classList.remove('error', 'has-upgrade');

      this.selectSizeVariant(result.size);

      if (this.productType === 'maillots') {
        const cupSize = this.cupSizeDropdown.value;
        const pantSize = this.pantSizeDropdown.value;
        const height = this.heightDropdown.value;
        this.createOrUpdateInput('properties[Maillot Cup Size]', cupSize);
        this.createOrUpdateInput('properties[Maillot Pant Size]', pantSize);
        this.createOrUpdateInput('properties[Maillot Height]', height);
      } else {
        const bandSize = this.bandSizeDropdown.value;
        const cupSize = this.cupSizeDropdown.value;
        this.createOrUpdateInput('properties[Top Band Size]', bandSize);
        this.createOrUpdateInput('properties[Top Cup Size]', cupSize);
      }
    }
  }

  selectSizeVariant(size) {
    // Convert size to string for comparison
    const sizeStr = size.toString();
    
    // Look for radio inputs with data-index="option2" (size option in this theme)
    const sizeInputs = document.querySelectorAll('input[type="radio"][data-index="option2"]');
    
    if (sizeInputs.length === 0) {
      console.warn('Could not find size variant radio inputs. Make sure option 2 is the size option.');
      return;
    }

    // Find the input that matches the recommended size
    let foundInput = null;
    sizeInputs.forEach(input => {
      if (input.value === sizeStr) {
        foundInput = input;
      }
    });

    if (foundInput) {
      // Click the associated label to trigger the selection
      const label = document.querySelector(`label[for="${foundInput.id}"]`);
      if (label) {
        label.click();
        console.log(`Selected size: ${sizeStr}`);
      } else {
        // Fallback: click the input directly
        foundInput.click();
        console.log(`Selected size: ${sizeStr}`);
      }
    } else {
      console.warn(`Could not find variant input for size: ${sizeStr}`);
    }
  }

  createOrUpdateInput(propertyName, value) {
    const form = document.querySelector('form.product-form');
    if (!form) {
        console.error('Could not find form to add property input');
        return;
    }

    let input = document.querySelector(`input[name="${propertyName}"]`);
    
    if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = propertyName;
        form.appendChild(input);
    }
    
    input.value = value;
  }

  showError() {
    this.resultMessage.textContent = 'Try another style!';
    this.resultMessage.classList.add('error');
    this.resultMessage.classList.remove('disabled', 'has-upgrade');
  }

  findCompanionProduct(cupSize) {
    const productJsonScript = document.querySelector('[data-product-json]');
    if (!productJsonScript) return null;
    try {
      const product = JSON.parse(productJsonScript.textContent);
      const titleLower = (product.title || '').toLowerCase();
      for (const [key, companion] of Object.entries(this.companionProducts)) {
        if (titleLower.includes(key) && companion.cupSizes.includes(cupSize)) {
          return companion;
        }
      }
    } catch (e) {}
    return null;
  }

  showCompanionSuggestion(companion) {
    this.resultMessage.innerHTML = `Try the&nbsp;<a href="${companion.url}">${companion.name}</a>!`;
    this.resultMessage.classList.add('error');
    this.resultMessage.classList.remove('disabled', 'has-upgrade');
  }

  appendCUpgradeSuggestion(cupSize) {
    if (cupSize !== 'C') return;
    const productJsonScript = document.querySelector('[data-product-json]');
    if (!productJsonScript) return;
    try {
      const product = JSON.parse(productJsonScript.textContent);
      const titleLower = (product.title || '').toLowerCase();
      for (const [key, companion] of Object.entries(this.cUpgradeProducts)) {
        if (titleLower.includes(key)) {
          this.resultMessage.classList.add('has-upgrade');
          this.resultMessage.innerHTML += `<span class="cup-upgrade-suggestion">Fuller C-cup? Some gals prefer the&nbsp;<a href="${companion.url}">${companion.name}</a></span>`;
          return;
        }
      }
    } catch (e) {}
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  new BraSizeSelector('.bra-size-selector-container');
});